import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';

import { useUpdateRoleMutation } from './_graphql/useUpdateRoleMutation';

import type { CurrentRoleData } from '~/modules/settings/_state/roleForm.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

interface RenameRoleDialogProps {
  isOpen: boolean;
  role: CurrentRoleData | null;
  onOpenChange: (open: boolean) => void;
}

export const RenameRoleDialog = ({
  isOpen,
  role,
  onOpenChange,
}: RenameRoleDialogProps) => {
  const t = useTranslations();
  const toast = useToast();
  const updateRoleMutation = useUpdateRoleMutation();

  const [name, setName] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (isOpen && role) {
      setName(role.name);
    }
  }, [isOpen, role]);

  const isUnchanged = name.trim() === role?.name;
  const canSave = name.trim().length > 0 && !isUnchanged && !isSubmitting;

  const handleSave = async () => {
    if (!role || !canSave) return;

    setIsSubmitting(true);
    try {
      await updateRoleMutation.mutateAsync({
        id: role.id,
        name: name.trim(),
      });
      toast.success(t.teamAndPermissions.roles.renameDialog.success);
      onOpenChange(false);
    } catch {
      toast.error(t.teamAndPermissions.roles.renameDialog.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setName('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSave) {
      handleSave();
    }
  };

  if (!role) return null;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@RenameRoleDialog"
    >
      <DialogContent className="max-w-md min-w-[330px]" closeButton={false}>
        <DialogHeader className="mr-0">
          <div className="text-sm font-semibold">
            {t.teamAndPermissions.roles.renameDialog.label}
          </div>
        </DialogHeader>

        <div className="px-4">
          <TextField
            placeholder={t.teamAndPermissions.roles.renameDialog.placeholder}
            value={name}
            onChange={setName}
            onKeyDown={handleKeyDown}
            autoFocus
            isRequired
          />
        </div>

        <div className="flex justify-end gap-2 px-4 py-4">
          <Button
            variant="text"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.teamAndPermissions.roles.renameDialog.cancel}
          </Button>
          <Button
            variant="solid"
            onPress={handleSave}
            isDisabled={!canSave}
            isLoading={isSubmitting}
          >
            {t.teamAndPermissions.roles.renameDialog.save}
          </Button>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
